import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FolderTree, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Categories() {
  const { isAdmin } = useAuth();
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");

  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name) return;
    try { await api.post("/categories", { name, description: desc }); toast.success("Category added"); setOpen(false); setName(""); setDesc(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/categories/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex items-center justify-between">
        <div><h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Categories</h2><p className="text-slate-500 mt-1">{cats.length} procedure categories</p></div>
        {isAdmin && <Button onClick={() => setOpen(true)} data-testid="add-category-btn" className="bg-blue-600 hover:bg-blue-700 text-white gap-2"><Plus className="h-4 w-4" /> Add Category</Button>}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cats.map((c) => (
          <Card key={c.id} className="p-6 rounded-xl border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200 group" data-testid={`cat-${c.id}`}>
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 rounded-xl bg-teal-50 dark:bg-teal-950/40 flex items-center justify-center"><FolderTree className="h-5 w-5 text-teal-600" /></div>
              {isAdmin && <Button variant="ghost" size="icon" onClick={() => del(c.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>}
            </div>
            <h3 className="font-heading font-bold mt-4">{c.name}</h3>
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{c.description}</p>
            <div className="mt-4 text-sm"><span className="font-semibold text-blue-600">{c.procedure_count}</span> <span className="text-slate-400">procedures</span></div>
          </Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white dark:bg-slate-900">
          <DialogHeader><DialogTitle className="font-heading">Add Category</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="cat-name" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} data-testid="save-category-btn" className="bg-blue-600 hover:bg-blue-700 text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
